import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { User } from '../models/user';
import { Role } from '../models/role';
import { environment } from '../../../environments/environment';
import {
  AddModuleRequest,
  ClientApplication,
  ModuleNode,
  ModulePermission,
  PermissionOption,
  RegisterApplicationRequest,
  SetUserPermissionsRequest,
  UpdateModuleRequest,
  UserApplicationAccess,
} from '../models/app-access.model';
import { buildModuleTree } from './app-access.utils';

export interface LoginResponse {
  data: {
    id: string;
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
    branch: string[];
    warehouse: string[];
    isVerified: boolean;
    jwToken: string;
    mustChangePassword?: boolean;
    issuedOn: string;
    expiresOn: string;
  };
  failed: boolean;
  message: string;
  succeeded: boolean;
}

export interface UserByUsernameResponse {
  data: {
    userId: string;
    userName: string;
  };
  failed: boolean;
  message: string;
  succeeded: boolean;
}


export interface IdentityUser {
  id: string;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  isActive: boolean;
  emailConfirmed: boolean;
  roles: string[];

  branchCodes?: string[] | null;
  regionCodes?: string[] | null;
  warehouseCodes?: string[] | null;
}

export interface PagedUsers {
  items: IdentityUser[];
  /** null when the API doesn't return a total count */
  total: number | null;

  
}

export interface IdentityRole {
  id: string;
  name: string;
}



export interface StandardEntry {
  code: string;
  name: string;
}

export interface AssignedEntry {
  id: number | null; // May be absent from some APIs; keep the code for preview/display.
  code: string;
}

function normalizeAssignmentCode(entry: any): string | null {
  if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
    const text = String(entry).trim();
    return text || null;
  }

  const nested =
    entry?.branch ??
    entry?.warehouse ??
    entry?.region ??
    entry?.Branch ??
    entry?.Warehouse ??
    entry?.Region ??
    entry?.assignment ??
    entry?.Assignment;

  const rawCode =
    entry?.code ??
    entry?.Code ??
    entry?.branchCode ??
    entry?.warehouseCode ??
    entry?.regionCode ??
    entry?.BranchCode ??
    entry?.WarehouseCode ??
    entry?.RegionCode ??
    nested?.code ??
    nested?.Code ??
    nested?.branchCode ??
    nested?.warehouseCode ??
    nested?.regionCode ??
    nested?.BranchCode ??
    nested?.WarehouseCode ??
    nested?.RegionCode ??
    nested?.branchCode ??
    nested?.warehouseCode ??
    nested?.regionCode;

  if (rawCode === null || rawCode === undefined || rawCode === '') return null;
  const text = String(rawCode).trim();
  return text || null;
}

function normalizeStringArray(value: any): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeAssignmentCode(item) ?? (typeof item === 'string' ? item.trim() || null : null))
      .filter((item): item is string => !!item);
  }

  if (value && typeof value === 'object') {
    const direct = normalizeAssignmentCode(value);
    return direct ? [direct] : [];
  }

  if (typeof value === 'string') {
    const text = value.trim();
    return text ? [text] : [];
  }

  return [];
}

function normalizeUserAssignments(user: IdentityUser | any): IdentityUser {
  if (!user || typeof user !== 'object') return user as IdentityUser;

  return {
    ...user,
    branchCodes: normalizeStringArray(user.branchCodes ?? user.branchCode ?? user.branch ?? user.branches ?? user.branchList ?? user.BranchCodes),
    warehouseCodes: normalizeStringArray(user.warehouseCodes ?? user.warehouseCode ?? user.warehouse ?? user.warehouses ?? user.warehouseList ?? user.WarehouseCodes),
    regionCodes: normalizeStringArray(user.regionCodes ?? user.regionCode ?? user.region ?? user.regions ?? user.regionList ?? user.RegionCodes),
  };
}

function normalizeAssignedEntries(res: any): AssignedEntry[] {
  const list = Array.isArray(res) ? res : (res?.data ?? res?.items ?? res?.results ?? []);
  if (!Array.isArray(list)) return [];

  return list
    .map((entry: any) => {
      const nested =
        entry?.branch ??
        entry?.warehouse ??
        entry?.region ??
        entry?.Branch ??
        entry?.Warehouse ??
        entry?.Region ??
        entry?.assignment ??
        entry?.Assignment;

      const rawId =
        entry?.id ??
        entry?.Id ??
        entry?.assignmentId ??
        entry?.AssignmentId ??
        entry?.branchId ??
        entry?.BranchId ??
        entry?.warehouseId ??
        entry?.WarehouseId ??
        entry?.regionId ??
        entry?.RegionId ??
        nested?.id ??
        nested?.Id ??
        nested?.assignmentId ??
        nested?.AssignmentId;

      const code = normalizeAssignmentCode(entry) ?? '';
      const id = rawId === null || rawId === undefined || rawId === '' ? null : Number(rawId);

      return { id: Number.isFinite(id as number) ? (id as number) : null, code };
    })
    .filter((entry) => !!entry.code);
}


export type StandardEntryType = 'Branch' | 'Warehouse' | 'Region';

export interface StandardEntryPage {
  data: StandardEntry[];
  page: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  failed: boolean;
  message: string | null;
  succeeded: boolean;
}

function normalize(res: any): PagedUsers {
  if (Array.isArray(res)) {
    return { items: res.map((item) => normalizeUserAssignments(item)), total: null };
  }

  // Unwrap { data, succeeded, message } responses
  const payload = res?.data ?? res;

  const items = Array.isArray(payload)
    ? payload
    : payload?.items ?? payload?.results ?? payload?.users ?? payload?.data ?? [];

  const total =
    res?.totalRecords ?? res?.totalCount ?? res?.total ??
    payload?.totalRecords ?? payload?.totalCount ?? payload?.total ?? null;

  const normalizedItems = Array.isArray(items)
    ? items.map((item) => normalizeUserAssignments(item))
    : [];

  return { items: normalizedItems, total };
}


// ---------------------------------------------------------------------
// App Access response normalizers.
// The Swagger only says "200 OK", so these accept the usual shapes
// ({ data: ... } wrapper, plain array, camelCase or PascalCase).
// Tighten them once you see the real responses.
// ---------------------------------------------------------------------

function unwrapList(res: any): any[] {
  if (Array.isArray(res)) return res;
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) return payload;
  const list = payload?.items ?? payload?.results ?? payload?.data ?? [];
  return Array.isArray(list) ? list : [];
}

function str(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

function normalizeApplication(item: any): ClientApplication {
  const active = item?.isActive ?? item?.IsActive;
  return {
    code: str(item?.code ?? item?.Code ?? item?.applicationCode),
    name: str(item?.name ?? item?.Name ?? item?.applicationName),
    isActive: active === undefined || active === null ? true : Boolean(active),
  };
}

function normalizePermissionIds(raw: any): number[] {
  const list = raw?.permissionIds ?? raw?.permissions ?? raw?.PermissionIds ?? [];
  if (!Array.isArray(list)) return [];
  return list
    .map((p: any) => Number(typeof p === 'object' && p !== null ? p.id ?? p.permissionId : p))
    .filter((id: number) => Number.isFinite(id));
}

function normalizeModulePermission(item: any): ModulePermission {
  return {
    moduleCode: str(item?.moduleCode ?? item?.ModuleCode ?? item?.code),
    permissionIds: normalizePermissionIds(item),
  };
}

function normalizeUserApplication(item: any): UserApplicationAccess {
  const modules = item?.modules ?? item?.Modules ?? [];
  return {
    applicationCode: str(item?.applicationCode ?? item?.ApplicationCode ?? item?.code),
    applicationName: str(item?.applicationName ?? item?.ApplicationName ?? item?.name),
    modules: Array.isArray(modules) ? modules.map(normalizeModulePermission) : [],
  };
}

const CURRENT_USER_KEY = 'currentUser';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly BASE_URL = `${environment.identityUrl}/identity`;
  private readonly ROLE_URL = environment.identityUrl;
  // Assumes identityUrl ends with "/api" (like ROLE_URL above is used for /role).
  private readonly APP_ACCESS_URL = `${environment.identityUrl}/app-access`;
  private readonly INVENTORY_URL = environment.inventoryUrl;
private readonly INVENTORY_API_VERSION = '1';
  private http = inject(HttpClient);
  private jwtHelper = inject(JwtHelperService);
  private router = inject(Router);

  // public
  public currentUser: Observable<User | null>;

  // private
  private currentUserSubject: BehaviorSubject<User | null>;

  constructor() {
    const stored = localStorage.getItem(CURRENT_USER_KEY);
    this.currentUserSubject = new BehaviorSubject<User | null>(
      stored ? JSON.parse(stored) : null
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  // ---------------------------------------------------------------------
  // Session state (from the template)
  // ---------------------------------------------------------------------

getStandardEntries(entryType: StandardEntryType): Observable<StandardEntry[]> {
  return this.http
    .get<any>(`${this.INVENTORY_URL}/v${this.INVENTORY_API_VERSION}/inventory/standardentries/${entryType}`)
    .pipe(
      map((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!Array.isArray(list)) return [];

        return list
          .filter((entry: any) => entry.isActive !== false)
          .map((entry: any) => ({
            code: String(entry.code),
            name: entry.description ?? String(entry.code),
          }));
      }),
    );
}

getStandardEntriesPage(
  entryType: StandardEntryType,
  pageNumber = 1,
  pageSize = 10,
): Observable<StandardEntryPage> {
  const params = new HttpParams()
    .set('pageNumber', pageNumber)
    .set('pageSize', pageSize);

  return this.http
    .get<any>(`${this.INVENTORY_URL}/v${this.INVENTORY_API_VERSION}/inventory/standardentries/${entryType}`, { params })
    .pipe(
      map((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];

        return {
          data: list
            .filter((entry: any) => entry.isActive !== false)
            .map((entry: any) => ({
              code: String(entry.code),
              name: entry.description ?? String(entry.code),
            })),
          page: Number(res?.page ?? pageNumber) || pageNumber,
          totalPages: Number(res?.totalPages ?? 1) || 1,
          totalCount: Number(res?.totalCount ?? 0) || 0,
          hasPreviousPage: Boolean(res?.hasPreviousPage),
          hasNextPage: Boolean(res?.hasNextPage),
          failed: Boolean(res?.failed),
          message: res?.message ?? null,
          succeeded: Boolean(res?.succeeded),
        };
      }),
    );
}

getAllStandardEntries(entryType: StandardEntryType): Observable<StandardEntry[]> {
  const pageSize = 1000;

  return this.getStandardEntriesPage(entryType, 1, pageSize).pipe(
    switchMap((firstPage) => {
      const all: StandardEntry[] = [...firstPage.data];
      const totalPages = Number(firstPage.totalPages ?? 1) || 1;

      if (totalPages <= 1) {
        return of(all);
      }

      const requests = [] as Observable<StandardEntryPage>[];
      for (let page = 2; page <= totalPages; page += 1) {
        requests.push(this.getStandardEntriesPage(entryType, page, pageSize));
      }

      return forkJoin(requests).pipe(
        map((pages) => {
          pages.forEach((pageResult) => {
            all.push(...(pageResult.data ?? []));
          });
          return all;
        }),
      );
    }),
  );
}

createStandardEntry(entryType: StandardEntryType, payload: Record<string, unknown> = {}): Observable<any> {
  const body: Record<string, unknown> = {
    code: '',
    description: '',
    isActive: true,
    ...payload,
  };

  const trimmedCode = typeof body.code === 'string' ? body.code.trim() : '';
  const trimmedDescription = typeof body.description === 'string' ? body.description.trim() : '';

  body.code = trimmedCode;
  body.description = trimmedDescription || trimmedCode;
  body.isActive = body.isActive !== false;

  if (entryType === 'Branch') {
    const regionId = Number((body.regionId ?? 0) as number | string);
    const avpGroupId = Number((body.avpGroupId ?? 0) as number | string);

    body.regionId = Number.isFinite(regionId) ? regionId : 0;
    body.avpGroupId = Number.isFinite(avpGroupId) ? avpGroupId : 0;
  } else {
    delete body.regionId;
    delete body.avpGroupId;
  }

  return this.http.post<any>(
    `${this.INVENTORY_URL}/v${this.INVENTORY_API_VERSION}/inventory/standardentries/${entryType}`,
    body
  );
}



getAllUsers(pageNumber: number, pageSize: number, search: string): Observable<PagedUsers> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
 
    const term = search.trim();
    if (term) {
      params = params.set('search', term);
    }
 
    return this.http
      .get<unknown>(`${this.BASE_URL}/get-all-users`, { params })
      .pipe(map((res) => normalize(res)));
  }

  getUserById(userId: string): Observable<IdentityUser> {
    return this.http
      .get<any>(`${this.BASE_URL}/get-user-by-id`, { params: { userId } })
      .pipe(map((res) => normalizeUserAssignments(res?.data ?? res) as IdentityUser));
  }

updateUser(data: {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  employeeCode: string;
  // isActive: boolean;
}): Observable<any> {
  return this.http.put<any>(`${this.BASE_URL}/update-user`, data);
}


deactivateUser(userId: string): Observable<any> {
  return this.http.put<any>(`${this.BASE_URL}/deactivate-user/${encodeURIComponent(userId)}`, {});
}

// Assumed URL: you only gave me the deactivate endpoint.
activateUser(userId: string): Observable<any> {
  return this.http.put<any>(`${this.BASE_URL}/activate-user/${encodeURIComponent(userId)}`, {});
}


  set user(user: User | null) {
    this.currentUserSubject.next(user);
  }

  /** Persists the given user to localStorage AND pushes it into the subject. */
  private setSession(user: User) {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  setApplicantInfo(applicantData: any, profilePicValue: string) {
    if (!this.currentUserValue) return;

    const userInfo: User = { ...this.currentUserValue };
    userInfo.applicant = { ...applicantData };
    userInfo.profilePicture = profilePicValue;

    // remove this line after
    userInfo.fullName = `${applicantData.firstName} ${applicantData.lastName}`;
    // end

    this.setSession(userInfo);
  }

  set tab(tab: string) {
    if (!this.currentUserValue?.applicant) return;

    const userInfo: User = { ...this.currentUserValue };
    userInfo.applicant = { ...userInfo.applicant, tab };
    this.setSession(userInfo);
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin(): boolean {
    return this.hasRole(Role.IdentitySuperAdmin);
  }

  /**
   *  Confirms if user is client
   */
  get isClient(): boolean {
    return this.hasRole(Role.IdentityBasic);
  }

  get isModerator(): boolean {
    return this.hasRole(Role.IdentityModerator);
  }

  get isAuthenticated(): boolean {
    const token = this.currentUserValue?.jwToken;
    if (!token) return false;
    try {
      return !this.jwtHelper.isTokenExpired(token);
    } catch {
      return false;
    }
  }

  get tokenDetails(): any {
    const token = this.currentUserValue?.jwToken;
    if (!token) return null;
    try {
      return this.jwtHelper.decodeToken(token);
    } catch {
      return null;
    }
  }

  clearSession(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.currentUserSubject.next(null);
  }

  redirectMember(): void {
    const tab = this.currentUserValue?.applicant?.tab;

    if (tab) {
      const registrationTabs = [
        'BasicInformation', 'Employment', 'beneficiary', 'livelihood',
        'characterReference', 'membershipType', 'requirements',
        'pmes', 'payment', 'waiver', 'declaration'
      ];
      this.router.navigate([registrationTabs.includes(tab) ? '/registration/' : '/']);
    } else {
      this.router.navigate(['/registration/']);
    }
  }

  // ---------------------------------------------------------------------
  // HTTP calls (from the tested version) — wired into the session state
  // ---------------------------------------------------------------------

  login(username: string, password: string): Observable<LoginResponse> {
    const body = { username, password };

    return this.http.post<LoginResponse>(`${this.BASE_URL}/token`, body).pipe(
      tap(res => {
        if (res.succeeded && res.data) {
          const userInfo: User = {
            id: res.data.id,
            userName: res.data.userName,
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            fullName: `${res.data.firstName} ${res.data.lastName}`,
            email: res.data.email,
            roles: res.data.roles as any,
            isVerified: res.data.isVerified,
            jwToken: res.data.jwToken,
            mustChangePassword: !!res.data.mustChangePassword,
            branch: res.data.branch?.join(', '),
            warehouse: res.data.warehouse?.join(', '),
          };

          this.setSession(userInfo);
        }
      })
    );
  }

  forgotPassword(email: string): Observable<any> {
    const body = { email, isEmailAddress: true };
    return this.http.post<any>(`${this.BASE_URL}/forgot-password`, body);
  }

  adminForgotPassword(userId: string): Observable<any> {
  return this.http.post<any>(`${this.BASE_URL}/admin-forgot-password/${encodeURIComponent(userId)}`, {});
}

 register(data: {
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  employeeCode?: string;
  roleId?: string;
  password?: string;
  confirmPassword?: string;
}): Observable<any> {
  const payload: Record<string, string | undefined> = {
    firstName: data.firstName?.trim(),
    lastName: data.lastName?.trim(),
    email: data.email?.trim(),
    userName: data.userName?.trim(),
    employeeCode: data.employeeCode?.trim() || undefined,
  };

  if (data.roleId) {
    payload.roleId = data.roleId;
  }

  if (data.password) {
    payload.password = data.password;
  }

  if (data.confirmPassword) {
    payload.confirmPassword = data.confirmPassword;
  }

  return this.http.post<any>(`${this.BASE_URL}/register-with-generated-password`, payload);
}

  logout(): void {
    this.clearSession();
  }

  refreshToken(refreshToken: string): Observable<LoginResponse> {
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<LoginResponse>(`${this.BASE_URL}/token`, body.toString(), { headers }).pipe(
      tap(res => {
        if (res.succeeded && res.data && this.currentUserValue) {
          const userInfo: User = {
            ...this.currentUserValue,
            jwToken: res.data.jwToken,
          };
          this.setSession(userInfo);
        }
      })
    );
  }

  /** Confirm email with OTP / code */
  confirmEmail(userId: string, code: string): Observable<any> {
    const url = `${this.BASE_URL}/confirm-email?userId=${encodeURIComponent(userId)}&code=${encodeURIComponent(code)}`;
    return this.http.post<any>(url, {});
  }

  resetPassword(data: {
    email?: string;
    username?: string;
    code?: string;
    password: string;
    confirmPassword: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.BASE_URL}/reset-password`, data);
  }

  changePassword(data: {
    username?: string;
    email?: string;
    password: string;
    confirmPassword: string;
    code?: string;
  }): Observable<any> {
    const payload = {
      username: data.username ?? data.email,
      email: data.email ?? data.username,
      password: data.password,
      confirmPassword: data.confirmPassword,
      code: data.code,
    };

    return this.http.post<any>(`${this.BASE_URL}/change-password`, payload).pipe(
      catchError((err) => {
        if (err.status !== 404) {
          throw err;
        }

        const fallback = {
          username: payload.username,
          email: payload.email,
          code: payload.code,
          password: payload.password,
          confirmPassword: payload.confirmPassword,
        };

        return this.http.post<any>(`${this.BASE_URL}/reset-password`, fallback);
      }),
    );
  }

private normalizeRoleList(res: any): IdentityRole[] {
  if (Array.isArray(res)) {
    return res as IdentityRole[];
  }

  if (Array.isArray(res?.data)) {
    return res.data as IdentityRole[];
  }

  if (Array.isArray(res?.data?.items)) {
    return res.data.items as IdentityRole[];
  }

  if (Array.isArray(res?.items)) {
    return res.items as IdentityRole[];
  }

  if (Array.isArray(res?.results)) {
    return res.results as IdentityRole[];
  }

  if (Array.isArray(res?.roles)) {
    return res.roles as IdentityRole[];
  }

  return [];
}

getAllRoles(): Observable<IdentityRole[]> {
  const pageSize = 1000;

  return this.http
    .get<any>(`${this.ROLE_URL}/role`, { params: { pageNumber: 1, pageSize } })
    .pipe(
      switchMap((firstPage) => {
        const all = [...this.normalizeRoleList(firstPage)];
        const totalPages = Number(firstPage?.totalPages ?? firstPage?.total_pages ?? 1) || 1;

        if (totalPages <= 1) {
          return of(all);
        }

        const requests = [] as Observable<any>[];
        for (let page = 2; page <= totalPages; page += 1) {
          requests.push(
            this.http.get<any>(`${this.ROLE_URL}/role`, { params: { pageNumber: page, pageSize } })
          );
        }

        return forkJoin(requests).pipe(
          map((pages) => {
            pages.forEach((pageResult) => {
              all.push(...this.normalizeRoleList(pageResult));
            });
            return all;
          }),
        );
      }),
    );
}


 
getUserBranches(userName: string): Observable<AssignedEntry[]> {
  return this.http
    .get<any>(`${this.BASE_URL}/branch/current-user`, { params: { userName } })
    .pipe(map(normalizeAssignedEntries));
}
 
getUserWarehouses(userName: string): Observable<AssignedEntry[]> {
  return this.http
    .get<any>(`${this.BASE_URL}/warehouse/current-user`, { params: { userName } })
    .pipe(map(normalizeAssignedEntries));
}
 
getUserRegions(userName: string): Observable<AssignedEntry[]> {
  return this.http
    .get<any>(`${this.BASE_URL}/region/current-user-region`, { params: { userName } })
    .pipe(map(normalizeAssignedEntries));
}
 
deleteBranch(id: number): Observable<any> {
  return this.http.delete<any>(`${this.BASE_URL}/branch/branch/${id}`);
}
 
deleteWarehouse(id: number): Observable<any> {
  return this.http.delete<any>(`${this.BASE_URL}/warehouse/warehouse/${id}`);
}
 
deleteRegion(id: number): Observable<any> {
  return this.http.delete<any>(`${this.BASE_URL}/region/region/${id}`);
}



  getUserRoles(): string[] {
    return (this.currentUserValue?.roles as unknown as string[]) ?? [];
  }

  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated;
  }

  assignBranch(data: any) {
    return this.http.post(`${this.BASE_URL}/branch`, data);
  }

  addRegion(data: any): Observable<any> {
    return this.http.post(`${this.BASE_URL}/region`, data);
  }

  addWarehouse(data: any) {
    return this.http.post(`${this.BASE_URL}/warehouse`, data);
  }

  getUserIdByUsername(username: string): Observable<UserByUsernameResponse> {
    return this.http.get<UserByUsernameResponse>(
      `${this.BASE_URL}/get-userId-by-username?username=${encodeURIComponent(username)}`
    );
  }

  getRoles(): Observable<IdentityRole[]> {
    return this.getAllRoles();
  }

  updateUserRoles(username: string, roles: string[]): Observable<any> {
    const trimmedUsername = String(username ?? '').trim();
    const payload = {
      username: trimmedUsername,
      roles: (roles ?? []).map((role) => String(role).trim()).filter(Boolean),
    };

    return this.http.post<any>(`${this.BASE_URL}/identity-role`, payload);
  }

  assignRoleToUser(userIdentifier: string, roleId: string): Observable<any> {
    return this.updateUserRoles(userIdentifier, [roleId]);
  }

  removeRoleFromUser(userIdentifier: string, roleId: string): Observable<any> {
    return this.updateUserRoles(
      userIdentifier,
      [roleId].filter(Boolean),
    );
  }

  createRole(description: string) {
    return this.http.post(`${this.ROLE_URL}/role/role`, { description });
  }

  getUsersByRole(role: string) {
    return this.http.get<any>(`${this.ROLE_URL}/role/get-userId-by-role?role=${role}`);
  }

  // ---------------------------------------------------------------------
  // App Access  (/api/app-access/*)
  // ---------------------------------------------------------------------

  /** All registered client applications. */
  getApplications(): Observable<ClientApplication[]> {
    return this.http
      .get<any>(`${this.APP_ACCESS_URL}/applications`)
      .pipe(map((res) => unwrapList(res).map(normalizeApplication)));
  }

  /** Register a client application. */
  registerApplication(data: RegisterApplicationRequest): Observable<any> {
    return this.http.post<any>(`${this.APP_ACCESS_URL}/applications`, {
      code: data.code.trim(),
      name: data.name.trim(),
    });
  }

  /** Edit an application (found by `code`). `newCode` renames it. */
  updateApplication(data: {
    code: string;
    newCode: string;
    name: string;
    isActive: boolean;
  }): Observable<any> {
    return this.http.put<any>(`${this.APP_ACCESS_URL}/applications`, {
      code: data.code.trim(),
      newCode: data.newCode.trim(),
      name: data.name.trim(),
      isActive: data.isActive,
    });
  }

  /** Delete an application with its modules and users' permissions. `force` is needed if users still have access. */
  deleteApplication(code: string, force: boolean): Observable<any> {
    const params = new HttpParams().set('Code', code.trim()).set('Force', force);
    return this.http.delete<any>(`${this.APP_ACCESS_URL}/applications`, { params });
  }

  /** Module tree of an application, sorted by sortOrder. */
  getModuleTree(applicationCode: string): Observable<ModuleNode[]> {
    return this.http
      .get<any>(`${this.APP_ACCESS_URL}/applications/${encodeURIComponent(applicationCode)}/modules`)
      .pipe(map((res) => buildModuleTree(unwrapList(res))));
  }

  /** Add a module or sub-module to an application. */
  addModule(data: AddModuleRequest): Observable<any> {
    return this.http.post<any>(`${this.APP_ACCESS_URL}/modules`, this.moduleBody(data));
  }

  /** Edit a module (found by applicationCode + code). `newCode` renames it. */
  updateModule(data: UpdateModuleRequest): Observable<any> {
    return this.http.put<any>(`${this.APP_ACCESS_URL}/modules`, {
      ...this.moduleBody(data),
      newCode: data.newCode.trim(),
    });
  }

  private moduleBody(data: AddModuleRequest) {
    return {
      applicationCode: data.applicationCode.trim(),
      code: data.code.trim(),
      name: data.name.trim(),
      route: data.route?.trim() || null,
      parentCode: data.parentCode?.trim() || null,
      sortOrder: Number(data.sortOrder) || 0,
    };
  }

  /** Permission choices (View, Create, Edit, ...) that can be given on a module. */
  getAppPermissions(): Observable<PermissionOption[]> {
    return this.http.get<any>(`${this.APP_ACCESS_URL}/permissions`).pipe(
      map((res) =>
        unwrapList(res)
          .map((p: any) => ({
            id: Number(p?.id ?? p?.Id ?? p?.permissionId),
            name: str(p?.name ?? p?.Name),
          }))
          .filter((p) => Number.isFinite(p.id)),
      ),
    );
  }

  /**
   * Sets a user's permissions on the listed modules only. Other modules are
   * unchanged. A module with an empty permissionIds list is removed.
   */
  setUserPermissions(data: SetUserPermissionsRequest): Observable<any> {
    return this.http.put<any>(`${this.APP_ACCESS_URL}/users/permissions`, {
      username: data.username.trim(),
      applicationCode: data.applicationCode.trim(),
      modules: data.modules,
    });
  }

  /** Removes ALL of a user's module permissions in one application. */
  removeUserApplicationAccess(username: string, applicationCode: string): Observable<any> {
    const params = new HttpParams()
      .set('Username', username.trim())
      .set('ApplicationCode', applicationCode.trim());
    return this.http.delete<any>(`${this.APP_ACCESS_URL}/users/permissions`, { params });
  }

  /** Applications and module permissions of a user. */
  getUserApplications(userName: string): Observable<UserApplicationAccess[]> {
    const params = new HttpParams().set('UserName', userName.trim());
    return this.http
      .get<any>(`${this.APP_ACCESS_URL}/users/applications`, { params })
      .pipe(map((res) => unwrapList(res).map(normalizeUserApplication)));
  }

  /** Applications and module permissions of the signed-in user (for menus and guards). */
  getMyApplications(): Observable<UserApplicationAccess[]> {
    return this.http
      .get<any>(`${this.APP_ACCESS_URL}/me/applications`)
      .pipe(map((res) => unwrapList(res).map(normalizeUserApplication)));
  }
}